// 위치 정보와 관련된 API 서비스
import { initGoogleMaps } from '../utils/googleMapsLoader';

// Google Maps 타입 선언
declare global {
  interface Window {
    google: {
      maps: {
        Map: any;
        Marker: any;
        event: any;
        Geocoder: new () => {
          geocode: (
            request: { 
              address: string;
              language?: string;
              region?: string;
            },
            callback: (results: any[], status: string) => void
          ) => void;
        };
        [key: string]: any;
      };
      [key: string]: any;
    };
  }
}

// Nominatim API 응답 타입 (reverseGeocode에서 사용)
// interface NominatimReverseResponse {
//   display_name: string;
//   address: {
//     road?: string;
//     house_number?: string;
//     neighbourhood?: string;
//     suburb?: string;
//     city?: string;
//     town?: string;
//     county?: string;
//     state?: string;
//     postcode?: string;
//     country?: string;
//     country_code?: string;
//   };
// }

// interface Location {
//   lng: number;
//   lat: number;
// }

// 네이버 API 관련 인터페이스 (참조용으로 유지)
// interface NaverReverseGeoResult {
//   name: string;
//   region: {
//     area0: { name: string };
//     area1: { name: string; alias?: string };
//     area2: { name: string };
//     area3: { name: string };
//     area4: { name: string };
//   };
//   land?: {
//     name?: string;
//     number1?: string;
//     number2?: string;
//   };
// }

// interface NaverReverseGeoResponse {
//   status: {
//     code: number;
//     name: string;
//     message: string;
//   };
//   results: NaverReverseGeoResult[];
// }

// 네이버 지오코딩 API 응답 타입 (참조용으로 유지)
// interface NaverGeocodeResponse {
//   status: string;
//   meta: {
//     totalCount: number;
//     page: number;
//     count: number;
//   };
//   addresses: Array<{
//     roadAddress: string;
//     jibunAddress: string;
//     englishAddress: string;
//     x: string; // longitude
//     y: string; // latitude
//     distance: number;
//   }>;
//   errorMessage?: string;
// }

// 네이버 API 키 (참조용으로 유지)
// const NAVER_API_KEY_ID = 't9urh3v0l9';
// const NAVER_API_KEY = 'uF1arW1RxI9MREhrfQpgfYwmHyoYHR4LE1heWfUz';

/**
 * 네이버 Maps API를 사용하여 좌표를 주소로 변환 (역지오코딩)
 * @param lng 경도
 * @param lat 위도
 * @param preferEnglish 영문 주소 선호 여부 (기본값: false)
 * @returns 주소 문자열 또는 null
 */
export const reverseGeocode = async (lng: number, lat: number, preferEnglish: boolean = false): Promise<string | null> => {
  try {
    // 네이버 Maps JavaScript SDK를 사용한 역지오코딩
    if (typeof window !== 'undefined' && window.naver && window.naver.maps && window.naver.maps.Service) {
      try {
        const naverResult = await new Promise<string | null>((resolve) => {
          // 10초 타임아웃 설정
          const timeout = setTimeout(() => {
            resolve(null);
          }, 10000);
          
          try {
            // 네이버 Maps SDK의 reverseGeocode 메서드 호출
            (window.naver.maps.Service as any).reverseGeocode({
              coords: new (window.naver.maps as any).LatLng(lat, lng),
              orders: [
                (window.naver.maps.Service as any).OrderType.ADDR,
                (window.naver.maps.Service as any).OrderType.ROAD_ADDR
              ].join(',')
            }, (status: any, response: any) => {
              clearTimeout(timeout);
              
              if (status === (window.naver.maps.Service as any).Status.OK && 
                  response.v2 && 
                  response.v2.results && 
                  response.v2.results.length > 0) {
                
                // 도로명 주소 우선, 없으면 지번 주소 사용
                const roadAddr = response.v2.results.find((result: any) => result.name === 'roadaddr');
                const jibunAddr = response.v2.results.find((result: any) => result.name === 'addr');
                
                const selectedResult = roadAddr || jibunAddr;
                
                if (selectedResult && selectedResult.region) {
                  // 네이버 API 응답에서 주소 조합
                  const region = selectedResult.region;
                  const land = selectedResult.land;
                  
                  const parts = [];
                  
                  // 시/도
                  if (region.area1?.name) parts.push(region.area1.name);
                  
                  // 구/군
                  if (region.area2?.name) parts.push(region.area2.name);
                  
                  // 동/읍/면
                  if (region.area3?.name) parts.push(region.area3.name);
                  
                  // 상세 주소 (도로명 또는 지번)
                  if (land?.name) {
                    let detailAddr = land.name;
                    if (land.number1) {
                      detailAddr += ` ${land.number1}`;
                      if (land.number2) {
                        detailAddr += `-${land.number2}`;
                      }
                    }
                    parts.push(detailAddr);
                  }
                  
                  const koreanAddress = parts.join(' ');
                  resolve(koreanAddress);
                } else {
                  resolve(null);
                }
              } else {
                resolve(null);
              }
            });
          } catch (callError) {
            clearTimeout(timeout);
            resolve(null);
          }
        });
        
        if (naverResult) {
          // 영문 주소가 필요한 경우 구글 API로 번역
          if (preferEnglish) {
            const englishAddress = await translateKoreanToEnglishWithGoogle(naverResult);
            return englishAddress || naverResult; // 번역 실패 시 한글 주소 반환
          }
          return naverResult;
        }
      } catch (error) {
        console.warn('네이버 Maps SDK 역지오코딩 실패:', error);
      }
    }

    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
    
    const response = await fetch(url, {
      headers: {
        'Accept-Language': preferEnglish ? 'en,en-US;q=0.9' : 'ko,ko-KR;q=0.9,en;q=0.8',
        'User-Agent': 'KOREA ORDER App (https://www.koreaorder.com)'
      }
    });
    
    if (!response.ok) {
      console.error('역지오코딩 API 호출 실패:', response.status, response.statusText);
      return null;
    }
    
    const data = await response.json();
    
    // Nominatim API 응답에서 주소 조합
    if (data && data.address) {
      const addr = data.address;
      let formattedAddress = '';
      
      // 한국 형식 주소
      if (addr.country_code === 'kr') {
        const parts = [];
        
        if (!preferEnglish) {
          // 한글 주소 형식
          if (addr.country) parts.push(addr.country);
          if (addr.state) parts.push(addr.state);
          if (addr.county) parts.push(addr.county);
          if (addr.city) parts.push(addr.city);
          if (addr.suburb) parts.push(addr.suburb);
          if (addr.neighbourhood) parts.push(addr.neighbourhood);
          if (addr.road) parts.push(addr.road);
          if (addr.house_number) parts.push(addr.house_number);
          
          formattedAddress = parts.join(' ');
        } else {
          // 영문 주소 형식
          if (addr.house_number) parts.push(addr.house_number);
          if (addr.road) parts.push(addr.road);
          if (addr.suburb) parts.push(addr.suburb);
          if (addr.city) parts.push(addr.city);
          if (addr.state) parts.push(addr.state);
          if (addr.country) parts.push(addr.country);
          
          formattedAddress = parts.join(', ');
        }
      } 
      // 기타 국가 (영문 형식)
      else {
        const parts = [];
        
        if (addr.house_number) parts.push(addr.house_number);
        if (addr.road) parts.push(addr.road);
        if (addr.suburb) parts.push(addr.suburb);
        if (addr.city || addr.town || addr.village) {
          parts.push(addr.city || addr.town || addr.village);
        }
        if (addr.state) parts.push(addr.state);
        if (addr.country) parts.push(addr.country);
        
        formattedAddress = parts.join(', ');
      }
      
      return formattedAddress;
    }
    
    return null;
  } catch (error) {
    console.error('역지오코딩에 실패했습니다:', error);
    
    // IP 기반 위치 정보 시도 - 좌표->주소 변환 실패 시 폴백 메커니즘
    try {
      const ipLocation = await getIpBasedLocation();
      if (ipLocation) {
        return preferEnglish 
          ? `Near ${ipLocation.city || 'current location'}, ${ipLocation.region || ''}, ${ipLocation.country || ''}`
          : `${ipLocation.city || '현재 위치'} 근처, ${ipLocation.region || ''}, ${ipLocation.country || ''}`;
      }
    } catch (ipError) {
      console.error('IP 기반 위치 정보 실패:', ipError);
    }
    
    return null;
  }
};

/**
 * 구글 Maps API를 사용하여 한글 주소를 영문 주소로 번역
 * @param koreanAddress 한글 주소
 * @returns 영문 주소 또는 null
 */
const translateKoreanToEnglishWithGoogle = async (koreanAddress: string): Promise<string | null> => {
  try {
    // Google Maps API 동적 로드
    const isLoaded = await initGoogleMaps();
    if (!isLoaded) {
      console.warn('Google Maps API 로드 실패');
      return null;
    }

    // Google Maps JavaScript SDK 사용
    if (typeof window !== 'undefined' && window.google && window.google.maps) {
      const result = await new Promise<string | null>((resolve) => {
        const geocoder = new window.google.maps.Geocoder();
        
        // 한글 주소로 지오코딩하여 영문 주소 얻기
        geocoder.geocode({ 
          address: koreanAddress,
          language: 'en',
          region: 'KR'
        }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            // 첫 번째 결과의 formatted_address가 영문 주소
            const englishAddress = results[0].formatted_address;
            resolve(englishAddress);
          } else {
            resolve(null);
          }
        });
      });
      
      return result;
    }
    
    return null;
  } catch (error) {
    console.warn('구글 API 한글->영문 번역 실패:', error);
    return null;
  }
};

/**
 * 브라우저의 Geolocation API를 사용하여 정확한 위치를 가져오는 함수
 */
export const getCurrentPosition = (): Promise<{ lat: number; lng: number }> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        resolve(location);
      },
      (error) => {
        console.error('현재 위치 정보 취득 실패:', error);
        reject(error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

// 예비: IP 기반 대략적 위치 정보 가져오기 (Geolocation이 실패할 경우)
export const getIpBasedLocation = async (): Promise<{ lat: number; lng: number; city?: string; region?: string; country?: string } | null> => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    
    if (!response.ok) {
      throw new Error(`IP 기반 위치 정보 API 오류: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.latitude && data.longitude) {
      return {
        lat: data.latitude,
        lng: data.longitude,
        city: data.city,
        region: data.region,
        country: data.country_name
      };
    }
    
    return null;
  } catch (error) {
    console.error('IP 기반 위치 정보 취득 실패:', error);
    return null;
  }
};

/**
 * Naver Maps API를 사용하여 주소를 좌표로 변환 (지오코딩)
 * @param address 변환할 주소
 * @returns 좌표 정보 또는 null
 */
export const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  try {
    // 주소가 비어있거나 너무 짧으면 처리하지 않음
    if (!address || address.trim().length < 3) {
      return null;
    }

    const trimmedAddress = address.trim();
    
    // Naver Maps JavaScript SDK 사용
    if (typeof window !== 'undefined' && window.naver && window.naver.maps && window.naver.maps.Service) {
      try {
        const result = await new Promise<{ lat: number; lng: number } | null>((resolve, reject) => {
          // 10초 타임아웃 설정
          const timeout = setTimeout(() => {
            reject(new Error('Naver Maps SDK 지오코딩 타임아웃'));
          }, 10000);
          
          try {
            // Naver Maps SDK의 geocode 메서드 호출
            (window.naver.maps.Service as any).geocode({
              query: trimmedAddress
            }, (status: any, response: any) => {
              clearTimeout(timeout);
              
              if (status === (window.naver.maps.Service as any).Status.OK && 
                  response.v2 && 
                  response.v2.addresses && 
                  response.v2.addresses.length > 0) {
                const item = response.v2.addresses[0];
                resolve({
                  lat: parseFloat(item.y),
                  lng: parseFloat(item.x)
                });
              } else {
                reject(new Error('Naver Maps SDK 지오코딩 실패'));
              }
            });
          } catch (callError) {
            clearTimeout(timeout);
            reject(callError);
          }
        });
        
        if (result) {
          return result;
        }
      } catch (error) {
        console.warn('Naver Maps SDK 지오코딩 실패:', error);
      }
    }

    return null;
  } catch (error) {
    console.error('지오코딩 중 오류 발생:', error);
    return null;
  }
};

/**
 * Google Maps 좌표를 Naver Maps에서 사용 가능한 좌표로 변환
 * @param lat Google Maps 위도
 * @param lng Google Maps 경도
 * @returns Naver Maps 호환 좌표
 */
const convertGoogleToNaverCoords = (lat: number, lng: number): { lat: number; lng: number } => {
  // Google Maps와 Naver Maps 모두 WGS84를 사용하지만,
  // 미세한 차이가 있을 수 있으므로 Naver Maps API로 검증
  if (typeof window !== 'undefined' && window.naver && window.naver.maps) {
    try {
      // Naver Maps의 LatLng 객체로 변환하여 정규화
      const naverLatLng = new (window.naver.maps as any).LatLng(lat, lng);
      return {
        lat: naverLatLng.lat(),
        lng: naverLatLng.lng()
      };
    } catch (error) {
      console.warn('Naver Maps 좌표 변환 실패, 원본 좌표 사용:', error);
    }
  }
  
  // 변환 실패 시 원본 좌표 반환
  return { lat, lng };
};

/**
 * Google Maps API를 사용하여 주소를 좌표로 변환 (지오코딩)
 * @param address 변환할 주소
 * @returns 좌표 정보 또는 null
 */
export const geocodeAddressWithGoogle = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  try {
    // 주소가 비어있거나 너무 짧으면 처리하지 않음
    if (!address || address.trim().length < 3) {
      return null;
    }

    const trimmedAddress = address.trim();
    
    // Google Maps API 동적 로드
    const isLoaded = await initGoogleMaps();
    if (!isLoaded) {
      console.warn('Google Maps API 로드 실패');
      return null;
    }
    
    // Google Maps JavaScript SDK 사용
    if (typeof window !== 'undefined' && window.google && window.google.maps) {
      try {
        const result = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
          const geocoder = new window.google.maps.Geocoder();
          
          geocoder.geocode({ address: trimmedAddress }, (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
              const location = results[0].geometry.location;
              const googleCoords = {
                lat: location.lat(),
                lng: location.lng()
              };
              
              // Naver Maps 호환 좌표로 변환
              const naverCompatibleCoords = convertGoogleToNaverCoords(googleCoords.lat, googleCoords.lng);
              resolve(naverCompatibleCoords);
            } else {
              resolve(null);
            }
          });
        });
        
        if (result) {
          return result;
        }
      } catch (error) {
        console.warn('Google Maps SDK 지오코딩 실패:', error);
      }
    }

    return null;
  } catch (error) {
    console.error('Google Maps 지오코딩 중 오류 발생:', error);
    return null;
  }
};

/**
 * 좌표가 주어진 주소와 일치하는지 역지오코딩으로 검증
 * @param lat 위도
 * @param lng 경도
 * @param originalAddress 원본 주소
 * @returns 검증 결과와 실제 주소
 */
const verifyCoordinatesWithReverseGeocoding = async (
  lat: number, 
  lng: number, 
  originalAddress: string
): Promise<{ isValid: boolean; actualAddress?: string }> => {
  try {
    const actualAddress = await reverseGeocode(lng, lat);
    if (!actualAddress) {
      return { isValid: false };
    }
    
    // 주소 유사성 검사 (간단한 키워드 매칭)
    const originalKeywords = originalAddress.toLowerCase()
      .replace(/[^a-z가-힣0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 1);
    
    const actualKeywords = actualAddress.toLowerCase()
      .replace(/[^a-z가-힣0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 1);
    
    // 공통 키워드 개수 계산
    const commonKeywords = originalKeywords.filter(keyword => 
      actualKeywords.some(actual => 
        actual.includes(keyword) || keyword.includes(actual)
      )
    );
    
    const similarity = commonKeywords.length / Math.max(originalKeywords.length, 1);
    const isValid = similarity >= 0.3; // 30% 이상 유사하면 유효
    
    return { isValid, actualAddress };
  } catch (error) {
    console.warn('좌표 검증 실패:', error);
    return { isValid: false };
  }
};

/**
 * 영문 주소를 한글 주소로 번역
 * @param englishAddress 영문 주소
 * @returns 한글 주소
 */
const translateEnglishToKorean = (englishAddress: string): string => {
  let koreanAddress = englishAddress;
  
  // 지역명 번역 (서울 지역 중심)
  const translations: { [key: string]: string } = {
    // 서울 구 단위
    'Gangnam': '강남구',
    'Gangdong': '강동구',
    'Gangbuk': '강북구',
    'Gangseo': '강서구',
    'Gwanak': '관악구',
    'Gwangjin': '광진구',
    'Guro': '구로구',
    'Geumcheon': '금천구',
    'Nowon': '노원구',
    'Dobong': '도봉구',
    'Dongdaemun': '동대문구',
    'Dongjak': '동작구',
    'Mapo': '마포구',
    'Seodaemun': '서대문구',
    'Seocho': '서초구',
    'Seongdong': '성동구',
    'Seongbuk': '성북구',
    'Songpa': '송파구',
    'Yangcheon': '양천구',
    'Yeongdeungpo': '영등포구',
    'Yongsan': '용산구',
    'Eunpyeong': '은평구',
    'Jongno': '종로구',
    'Jung': '중구',
    'Jungnang': '중랑구',
    
    // 서울 동 단위
    'Sinsa-dong': '신사동',
    'Apgujeong': '압구정동',
    'Cheongdam': '청담동',
    'Samseong': '삼성동',
    'Yeoksam': '역삼동',
    'Nonhyeon': '논현동',
    'Daechi': '대치동',
    'Dogok': '도곡동',
    'Gaepo': '개포동',
    'Ilwon': '일원동',
    'Jamsil': '잠실동',
    'Banpo': '반포동',
    'Yangjae': '양재동',
    'Sajik': '사직동',
    'Sajik-dong': '사직동',
    
    // 도로명
    'Dosan-daero': '도산대로',
    'Teheran-ro': '테헤란로',
    'Gangnam-daero': '강남대로',
    'Bongeunsa-ro': '봉은사로',
    'Eonju-ro': '언주로',
    'Nonhyeon-ro': '논현로',
    'Apgujeong-ro': '압구정로',
    'Cheongdam-ro': '청담로',
    'Samseong-ro': '삼성로',
    'Yeoksam-ro': '역삼로',
    'Seocho-daero': '서초대로',
    'Banpo-daero': '반포대로',
    'Sajik-ro': '사직로',
    
    // 길 단위
    'gil': '길',
    'daero': '대로',
    'ro': '로',
    
    // 도시/국가
    'Seoul': '서울특별시',
    'South Korea': '대한민국',
    'Korea': '한국',
    
    // 기타 지명
    'Busan': '부산광역시',
    'Daegu': '대구광역시',
    'Incheon': '인천광역시',
    'Gwangju': '광주광역시',
    'Daejeon': '대전광역시',
    'Ulsan': '울산광역시',
    'Sejong': '세종특별자치시'
  };
  
  // 번역 적용 (대소문자 구분 없이)
  Object.entries(translations).forEach(([english, korean]) => {
    const regex = new RegExp(`\\b${english}\\b`, 'gi');
    koreanAddress = koreanAddress.replace(regex, korean);
  });
  
  // 불필요한 영문 제거 및 정리
  koreanAddress = koreanAddress
    .replace(/\b(dong|gu|ro|daero|gil)\b/gi, '') // 중복된 영문 단위 제거
    .replace(/,\s*/g, ' ') // 쉼표를 공백으로
    .replace(/\s+/g, ' ') // 연속 공백 정리
    .trim();
  
  return koreanAddress;
};

/**
 * 한국 주소를 네이버 API가 인식할 수 있는 여러 형식으로 변환
 * @param address 원본 주소
 * @returns 시도할 주소 변형들의 배열 (우선순위 순)
 */
const generateKoreanAddressVariations = (address: string): string[] => {
  const variations: string[] = [];
  
  // 패턴 분석
  const parts = address.split(/\s+/);
  
  // 번지수 추출 (예: "14-4")
  const houseNumber = parts.find(part => /^\d+(-\d+)?$/.test(part));
  
  // 도로명 추출 (예: "도산대로")
  const roadName = parts.find(part => /대로|로$/.test(part));
  
  // 길 번호 추출 (예: "13-길", "13길")
  const gilNumber = parts.find(part => /\d+(-)?길$/.test(part));
  
  // 동 이름 추출 (예: "신사동")
  const dongName = parts.find(part => /동$/.test(part));
  
  // 구 이름 추출 (예: "강남구")
  const guName = parts.find(part => /구$/.test(part));
  
  // 시 이름 추출 (예: "서울특별시")
  const cityName = parts.find(part => /시$/.test(part));
  
  // 우선순위 1: 가장 구체적인 도로명 주소 (도로명 + 길번호 + 번지)
  if (roadName && gilNumber && houseNumber) {
    variations.push(`${roadName} ${gilNumber} ${houseNumber}`);
    variations.push(`${roadName}${gilNumber} ${houseNumber}`);
    // 서울 포함 버전
    if (cityName) {
      variations.push(`${cityName} ${roadName} ${gilNumber} ${houseNumber}`);
    }
  }
  
  // 우선순위 2: 도로명 + 번지 (길번호 없이)
  if (roadName && houseNumber) {
    variations.push(`${roadName} ${houseNumber}`);
    if (cityName) {
      variations.push(`${cityName} ${roadName} ${houseNumber}`);
    }
  }
  
  // 우선순위 3: 구 + 도로명 + 길번호 + 번지
  if (guName && roadName && gilNumber && houseNumber) {
    variations.push(`${guName} ${roadName} ${gilNumber} ${houseNumber}`);
  }
  
  // 우선순위 4: 구 + 도로명 + 번지
  if (guName && roadName && houseNumber) {
    variations.push(`${guName} ${roadName} ${houseNumber}`);
  }
  
  // 우선순위 5: 동 + 도로명 + 길번호 + 번지
  if (dongName && roadName && gilNumber && houseNumber) {
    variations.push(`${dongName} ${roadName} ${gilNumber} ${houseNumber}`);
  }
  
  // 우선순위 6: 동 + 도로명 + 번지
  if (dongName && roadName && houseNumber) {
    variations.push(`${dongName} ${roadName} ${houseNumber}`);
  }
  
  // 우선순위 7: 도로명만 (번지 없이)
  if (roadName) {
    variations.push(roadName);
    if (cityName) {
      variations.push(`${cityName} ${roadName}`);
    }
    if (guName) {
      variations.push(`${guName} ${roadName}`);
    }
  }
  
  // 우선순위 8: 구 + 도로명 + 동 (번지 없이)
  if (guName && dongName && roadName) {
    variations.push(`${guName} ${dongName} ${roadName}`);
  }
  
  // 우선순위 9: 동 + 도로명 (번지 없이)
  if (dongName && roadName) {
    variations.push(`${dongName} ${roadName}`);
  }
  
  // 우선순위 10: 서울 + 구 + 동 (도로명 없이)
  if (cityName && guName && dongName) {
    variations.push(`${cityName} ${guName} ${dongName}`);
  }
  
  // 우선순위 11: 구 + 동 (도로명 없이)
  if (guName && dongName) {
    variations.push(`${guName} ${dongName}`);
  }
  
  // 마지막 우선순위: 동 이름만 (가장 부정확)
  if (dongName) {
    variations.push(dongName);
  }
  
  // 원본 주소도 마지막에 시도
  variations.push(address);
  
  // 중복 제거하되 순서 유지
  const uniqueVariations = Array.from(new Set(variations));
  
  return uniqueVariations;
};

/**
 * 통합 지오코딩 함수 - Google API 우선, 네이버는 백업용
 * @param address 변환할 주소
 * @returns 좌표 정보와 사용된 서비스 정보
 */
export const geocodeAddressUnified = async (address: string): Promise<{ lat: number; lng: number; source: 'naver' | 'google'; verified?: boolean } | null> => {
  try {
    // 한국어 주소인지 확인
    const hasKorean = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(address);
    const hasKoreanPlaceNames = /Seoul|Busan|Daegu|Incheon|Gwangju|Daejeon|Ulsan|Sejong|Gangnam|Gangdong|Gangbuk|Gangseo|Gwanak|Gwangjin|Guro|Geumcheon|Nowon|Dobong|Dongdaemun|Dongjak|Mapo|Seodaemun|Seocho|Seongdong|Seongbuk|Songpa|Yangcheon|Yeongdeungpo|Yongsan|Eunpyeong|Jongno|Jung|Jungnang|Sinsa|Apgujeong|Cheongdam|Samseong|Yeoksam|Nonhyeon|Daechi|Dogok|Gaepo|Ilwon|Jamsil|Banpo|Yangjae|Sajik|Dosan|Teheran|Bongeunsa|dong|gu|ro|daero|gil/i.test(address);
    
    // 모든 주소에 대해 Google API 우선 시도
    const googleResult = await geocodeAddressWithGoogle(address);
    if (googleResult) {
      // Google 결과 검증
      const verification = await verifyCoordinatesWithReverseGeocoding(
        googleResult.lat, 
        googleResult.lng, 
        address
      );
      
      if (verification.isValid) {
        return { 
          ...googleResult, 
          source: 'google', 
          verified: true 
        };
      }
    }
    
    // Google 실패 또는 검증 실패 시 네이버 시도
    if (hasKorean || hasKoreanPlaceNames) {
      // 1. 원본 주소로 네이버 시도
      const naverResult = await geocodeAddress(address);
      if (naverResult) {
        return { ...naverResult, source: 'naver', verified: true };
      }
      
      // 2. 영문이 포함된 경우 한글로 번역 후 네이버 재시도
      if (!hasKorean && hasKoreanPlaceNames) {
        const koreanAddress = translateEnglishToKorean(address);
        
        if (koreanAddress !== address) {
          // 여러 주소 변형 생성 및 시도
          const addressVariations = generateKoreanAddressVariations(koreanAddress);
          
          for (const variation of addressVariations) {
            const naverKoreanResult = await geocodeAddress(variation);
            if (naverKoreanResult) {
              return { ...naverKoreanResult, source: 'naver', verified: true };
            }
          }
        }
      }
    }

    // Google 검증 실패했지만 좌표는 있는 경우 사용
    if (googleResult) {
      return { 
        ...googleResult, 
        source: 'google', 
        verified: false 
      };
    }

    // 둘 다 실패
    return null;
  } catch (error) {
    console.error('통합 지오코딩 중 오류 발생:', error);
    return null;
  }
}; 